import React, { useRef } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Button from "../../../../../../../core/ui/Button";
import FormTextField from "../../../../../../../core/ui/FormTextField";
import {
  AttachmentIcon,
  FileIcon,
} from "../../../../../../../core/global/Icons";

const NOTE_BANNER =
  "Note: The Customer Identification Documents section is optional during the KYC setup but is required and must be completed before proposals can be tagged as won.";

const checkboxSx = {
  color: "#003F2E",
  "&.Mui-checked": { color: "#003F2E" },
};

const docLabelSx = { fontSize: 14, color: "#292C2F" };

export default function CustomerIdentificationStep({
  form,
  handleChange,
  handleFileChange,
  errors = {},
}) {
  const docs = form.documents || {};
  const fileInputRefs = useRef({});

  const toggleDoc = (key) => {
    handleChange("documents")({
      target: {
        value: {
          ...docs,
          [key]: { ...docs[key], checked: !docs[key]?.checked },
        },
      },
    });
  };

  const updateDocField = (key, field, value) => {
    handleChange("documents")({
      target: {
        value: {
          ...docs,
          [key]: { ...docs[key], [field]: value },
        },
      },
    });
  };

  const handleAttach = (key) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleChange("documents")({
        target: {
          value: {
            ...docs,
            [key]: { ...docs[key], file: file.name, fileSize: file.size },
          },
        },
      });
    }
  };

  const removeFile = (key) => {
    updateDocField(key, "file", null);
  };

  const CORP_DOCS = [
    {
      key: "secRegistration",
      label: "Securities and Exchange Commission Registration",
    },
    {
      key: "birRegistration",
      label: "Bureau of Internal Revenue Registration",
      hasUpload: true,
      hasTIN: true,
      hasAddress: true,
    },
    {
      key: "articlesOfIncorporation",
      label: "Articles of Incorporation",
      hasUpload: true,
    },
    {
      key: "registrationDataSheet",
      label: "Registration Data Sheet/Latest General Information Sheet",
    },
    {
      key: "secretaryCertificate",
      label:
        "Secretary's Certificate citing the pertinent portion of the Board Resolution authorizing the signatory to sign on behalf of the entity",
    },
    {
      key: "passportCopies",
      label: "Passport Copies of Authorized Signatories",
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          backgroundColor: "#FFFCEE",
          borderRadius: "4px",
          border: "1px solid #B38600",
          p: "12px",
          gap: "8px",
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#292C2F" }}>
          {NOTE_BANNER}
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          p: 3,
          boxShadow:
            "0px 2px 4px rgba(0,0,0,0.08), 0px 0px 6px rgba(0,0,0,0.02)",
        }}
      >
        <Typography
          sx={{ fontSize: "16px", fontWeight: 600, color: "#292C2F", mb: 3 }}
        >
          Customer Identification Documents
        </Typography>

        <Typography
          sx={{ fontSize: 13, fontWeight: 600, color: "#292C2F", mb: "12px" }}
        >
          Corporation (Registered in the Philippines){" "}
          <span style={{ color: "#d32f2f" }}>*</span>
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {CORP_DOCS.map((doc) => {
            const docState = docs[doc.key] || {};
            return (
              <Box
                key={doc.key}
                sx={{
                  border: "1px solid #D6D9DB",
                  borderRadius: "4px",
                  p: "16px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!docState.checked}
                      onChange={() => toggleDoc(doc.key)}
                      sx={checkboxSx}
                      size="small"
                    />
                  }
                  label={<Typography sx={docLabelSx}>{doc.label}</Typography>}
                />

                {docState.checked &&
                  (doc.hasUpload || doc.hasTIN || doc.hasAddress) && (
                    <Box
                      sx={{
                        borderTop: "1px solid #D6D9DB",
                        mt: 2,
                        pt: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {/* File upload */}
                      {doc.hasUpload && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                              Upload Supported file
                            </Typography>
                            <Box>
                              <Button
                                variant="outlined"
                                startIcon={<AttachmentIcon />}
                                disabled={!!docState.file}
                                sx={{ height: 36, fontSize: 13, maxWidth: 125, flexShrink: 0 }}
                                onClick={() =>
                                  fileInputRefs.current[doc.key]?.click()
                                }
                              >
                                Attach
                              </Button>
                              <input
                                type="file"
                                hidden
                                ref={(el) =>
                                  (fileInputRefs.current[doc.key] = el)
                                }
                                onChange={handleAttach(doc.key)}
                              />
                            </Box>
                            <Typography sx={{ fontSize: 12, color: "#8F969D" }}>
                              {docState.file ? "1/1" : "0/1"}
                            </Typography>
                          </Box>

                          {/* Right: divider + file card — only appears when file is attached */}
                          {docState.file && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box
                                sx={{
                                  width: "1px",
                                  height: 36,
                                  bgcolor: "#D6D9DB",
                                  mx: 3,
                                  flexShrink: 0,
                                }}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  border: "1px solid #D6D9DB",
                                  borderRadius: "4px",
                                  p: "12px",
                                  bgcolor: "#fff",
                                  width: 260,
                                  flexShrink: 0,
                                  boxShadow:
                                    "0px 2px 4px rgba(0,0,0,0.08), 0px 0px 6px rgba(0,0,0,0.02)",
                                }}
                              >
                                <FileIcon />
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 400,
                                      color: "#292C2F",
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {docState.file}
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: 10, color: "#8F969D" }}
                                  >
                                    {docState.fileSize
                                      ? `${Math.round(docState.fileSize / 1024)}kb`
                                      : ""}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile(doc.key)}
                                  sx={{ p: 0.25 }}
                                >
                                  <Close sx={{ fontSize: 24, color: "#000000" }} />
                                </IconButton>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* TIN */}
                      {doc.hasTIN && (
                        <Box>
                          <FormTextField
                            label="TIN"
                            required
                            value={docState.tin || ""}
                            onChange={(e) =>
                              updateDocField(doc.key, "tin", e.target.value)
                            }
                            placeholder="000-000-000-00000"
                            maxwidth="280px"
                          />
                        </Box>
                      )}

                      {/* Registered address in BIR 2303 */}
                      {doc.hasAddress && (
                        <Box>
                          <FormTextField
                            label="Registered address appearing in the BIR form No. 2303"
                            required
                            value={docState.birAddress || ""}
                            onChange={(e) =>
                              updateDocField(
                                doc.key,
                                "birAddress",
                                e.target.value,
                              )
                            }
                            placeholder=""
                            maxwidth="440px"
                          />
                        </Box>
                      )}
                    </Box>
                  )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
